import Phaser from 'phaser';
import type { ChallengeList } from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { markChallengesSeen, myChallenges } from '../online';
import { whoText } from '../nicknames';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { drawClubBar } from '../ui/clubChrome';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { formatTime } from '../ui/widgets';
import { shortDate } from './ChallengeScene';
import { wipeTo } from './WipeScene';

/**
 * CLUB > VERSUS > CHALLENGES (docs/ONLINE_UI.md): the Daily challenges the player sent and
 * answered, newest first, each with friends' times: green where the player came out ahead, red
 * where they were beaten.
 */
export class ChallengeListScene extends Phaser.Scene {
  private list: ChallengeList | null = null;
  private loading = true;

  constructor() {
    super('ChallengeList');
  }

  init(): void {
    this.list = null;
    this.loading = true;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void myChallenges().then((list) => {
      this.list = list;
      this.loading = false;
      if (list) markChallengesSeen();
      if (!this.scene.isActive()) return;
      clearScene(this);
      this.draw();
    });
  }

  private draw(): void {
    drawHeader(this, 'CLUB', 'club');
    drawTabBar(this, 'club');
    let y = drawClubBar(this, 'versus') + 4;
    const { width, height } = this.scale.gameSize;
    const backY = height - TABBAR_H - 28;
    bevelButton(this, 6, backY, width - 12, 22, 'BACK', () => wipeTo(this, 'Versus'));
    label(this, 6, y, 'CHALLENGES', 1, UI.dim);
    y += 12;
    const list = this.list;
    if (!list || list.challenges.length === 0) {
      const text = this.loading
        ? 'Loading...'
        : !list
          ? "Can't reach the server right now."
          : 'None yet. Clear the Daily, then CHALLENGE on its results to send one to your friends.';
      label(this, width / 2, y + 4, text, 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      return;
    }
    // Every color is from this player's side (owner, 2026-10-25): green, they came out ahead of
    // that person; red, that person beat them; plain, a tie or nothing to do with them.
    label(this, 6, y, 'Green: you won. Red: you lost.', 1, UI.dim);
    y += 12;
    const g = this.add.graphics();
    const me = saves().save.online.player?.id;
    const versus = (mine: number, theirs: number) =>
      mine < theirs ? UI.good : mine > theirs ? UI.danger : UI.text;
    for (const c of list.challenges) {
      const answers = c.answers.slice(0, 3);
      const h = 14 + Math.max(1, answers.length) * 10 + 4;
      if (y + h > backY - 4) break;
      navyPanel(g, 4, y - 2, width - 8, h);
      const sent = c.from.player === me;
      const head = sent
        ? `YOU SENT ${shortDate(c.date)}`
        : `FROM ${whoText(c.from.player, c.from.name)}`;
      label(this, 10, y + 2, head, 1, UI.accent);
      label(this, width - 10, y + 2, formatTime(c.ticks, TICK_RATE), 1, UI.accent).setOrigin(1, 0);
      let ay = y + 14;
      if (answers.length === 0) {
        label(this, 10, ay, sent ? 'No answers yet.' : 'Not raced yet.', 1, UI.dim);
      }
      for (const a of answers) {
        const mine = a.player === me;
        // Your own challenge: each friend against your time. A friend's: your answer against
        // theirs; other friends' answers are not about you.
        const color = sent ? versus(c.ticks, a.ticks) : mine ? versus(a.ticks, c.ticks) : UI.dim;
        label(this, 10, ay, mine ? 'YOU' : whoText(a.player, a.name), 1, color);
        label(this, width - 10, ay, formatTime(a.ticks, TICK_RATE), 1, color).setOrigin(1, 0);
        ay += 10;
      }
      y += h + 4;
    }
  }
}
